import { classifyDocumentExpiry, documentExpiryKey } from "./documentStatus";

export type CredentialAlertStatus = "EXPIRED" | "EXPIRING";

export interface CredentialRow {
  id: string;
  vehicleId?: unknown;
  driverId?: unknown;
  accessProfileId?: unknown;
  code?: unknown;
  validUntil?: unknown;
}

export interface CredentialAlert {
  id: string;
  code: string;
  vehicleId: string;
  driverId: string;
  validUntil: string;
  status: CredentialAlertStatus;
  /** Negativo = dias de atraso; 0 = vence hoje; positivo = dias restantes. */
  daysLeft: number;
}

function dayNumber(key: string): number {
  const [year, month, day] = key.split("-").map(Number);
  return Math.floor(Date.UTC(year, month - 1, day) / 86_400_000);
}

export function daysUntil(expiry: string, today = new Date()): number {
  const todayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
  return dayNumber(expiry.slice(0, 10)) - dayNumber(todayKey);
}

/**
 * Credenciais vencidas e a vencer. Quando o mesmo veículo/motorista/perfil foi reemitido, vale só a de maior
 * validade: uma credencial antiga vencida não gera alerta se já existe uma vigente.
 */
export function classifyCredentials(rows: CredentialRow[], warningDays = 30, today = new Date()): { expired: CredentialAlert[]; expiring: CredentialAlert[] } {
  const latest = new Map<string, { row: CredentialRow; expiry: string }>();
  for (const row of rows) {
    const expiry = documentExpiryKey(row.validUntil);
    if (!expiry) continue;
    const key = `${row.vehicleId ?? ""}|${row.driverId ?? ""}|${row.accessProfileId ?? ""}`;
    const current = latest.get(key);
    if (!current || expiry > current.expiry) latest.set(key, { row, expiry });
  }
  const expired: CredentialAlert[] = [];
  const expiring: CredentialAlert[] = [];
  for (const { row, expiry } of latest.values()) {
    const status = classifyDocumentExpiry(expiry, warningDays, today);
    if (status === "VALID") continue;
    const alert: CredentialAlert = {
      id: row.id, code: String(row.code ?? ""), vehicleId: String(row.vehicleId ?? ""), driverId: String(row.driverId ?? ""),
      validUntil: expiry, status, daysLeft: daysUntil(expiry, today)
    };
    (status === "EXPIRED" ? expired : expiring).push(alert);
  }
  const byDate = (a: CredentialAlert, b: CredentialAlert) => a.validUntil.localeCompare(b.validUntil);
  return { expired: expired.sort(byDate), expiring: expiring.sort(byDate) };
}

/** Texto curto para listas: "Venceu há 3 dias", "Vence hoje", "Vence em 5 dias". */
export function expiryText(daysLeft: number): string {
  if (daysLeft < 0) return `Venceu há ${Math.abs(daysLeft)} dia${Math.abs(daysLeft) === 1 ? "" : "s"}`;
  if (daysLeft === 0) return "Vence hoje";
  return `Vence em ${daysLeft} dia${daysLeft === 1 ? "" : "s"}`;
}
